import DeviceManage from '../DeviceManage';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { NoColumn } from '@components/Common/CTable/components/IdxColumn';
import { TableColumnTypes } from '@typedef/components/Common/CTable/table.column.type';
import CheckBoxContainer from '@components/Common/CInput/containers/CheckBoxContainer';
import { apiRoute, requestSecureDelete, requestSecureGet } from '@libs/api';
import useToken from '@hooks/useToken';
import { useNavigate } from 'react-router-dom';
import { ShopDeviceTypes } from '@typedef/components/DeviceManage/shop.device.types';

const DeviceManageContainer = () => {
  const navigate = useNavigate();
  const { getAccessToken } = useToken();
  const [tableData, setTableData] = useState<ShopDeviceTypes[]>([]);
  const [selectRows, setSelectRows] = useState<number[]>([]);
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 체크박스 선택
  const onChangeCheckBox = useCallback(
    (idx: number) => {
      if (selectRows.includes(idx)) {
        const filter = selectRows.filter((row) => row !== idx);
        return setSelectRows(filter);
      }
      setSelectRows((row) => [...row, idx]);
    },
    [selectRows],
  );

  // 단말 관리(등록, 수정)
  const manage = useCallback(
    (type: 'insert' | 'update') => {
      let url = `/device-info?type=${type}`;
      if (type === 'update') {
        if (selectRows.length > 1) {
          alert('수정할 정보를 하나만 선택해 주세요.');
          return;
        } else if (selectRows.length === 0) {
          alert('수정할 정보를 선택해 주세요.');
          return;
        }
        url += `&deviceId=${selectRows[0]}`;
      }
      navigate(url);
    },
    [navigate, selectRows],
  );

  // 단말 삭제
  const deleteDeviceData = useCallback(async () => {
    if (selectRows.length === 0) {
      alert('삭제할 데이터를 선택해 주세요.');
      return;
    }
    if (
      window.confirm(
        '해당 단말 정보를 등록한 사용자들의 단말 정보가 사라집니다.\n삭제하시겠습니까?',
      )
    ) {
      await Promise.all(
        selectRows.map(
          (idx) =>
            new Promise((resolve, reject) => {
              requestSecureDelete(
                apiRoute.shopDevice.deleteShopDevice + idx,
                {},
                getAccessToken()!,
              ).then((data) => {
                if (data.config.status >= 200 && data.config.status < 400) {
                  resolve(data);
                } else {
                  reject('http request failed with ' + data.config.status);
                }
              });
            }),
        ),
      )
        .then(() => {
          alert('성공적으로 삭제가 완료되었습니다.');
          window.location.reload();
        })
        .catch(() => {
          alert('삭제 실패');
        });
    }
  }, [getAccessToken, selectRows]);

  // 테이블 검색
  const searchTable = useCallback(() => {
    if (inputRef.current) {
      setSearchValue(inputRef.current.value);
    }
  }, []);

  // 테이블 검색내역 삭제
  const clearSearchValue = useCallback(() => {
    const { current } = inputRef;
    if (current) {
      current.value = '';
    }
    setSearchValue(null);
  }, []);

  const columns: TableColumnTypes[] = useMemo(
    () => [
      {
        Header: '',
        accessor: 'checkbox',
        disableSortBy: true,
        width: 50,
        Cell: ({ row }: { row: any }) => {
          return (
            <CheckBoxContainer
              id={row.original.idx}
              defaultChecked={selectRows.includes(row.original.idx)}
              onChangeFunc={onChangeCheckBox}
            />
          );
        },
      },
      NoColumn('idx'),
      {
        accessor: 'device_info.device.pet_name',
        Header: '단말명',
      },
      {
        accessor: 'device_info.color.name',
        Header: '단말색상',
      },
      {
        accessor: 'serial_number',
        Header: '단말일련번호',
      },
    ],
    [onChangeCheckBox, selectRows],
  );

  const getTableData = useCallback(async () => {
    await new Promise((resolve, reject) => {
      requestSecureGet<ShopDeviceTypes[]>(
        apiRoute.shopDevice.getShopDevices,
        {},
        getAccessToken()!,
      ).then((res) => {
        if (res.config.status >= 200 && res.config.status < 400) {
          setTableData(res.data);
        } else {
          reject(`http request failed with ${res.config.status}`);
        }
      });
    });
  }, [getAccessToken]);

  useEffect(() => {
    getTableData();
  }, [getTableData]);

  return (
    <DeviceManage
      columns={columns}
      tableData={tableData}
      manage={manage}
      deleteDeviceData={deleteDeviceData}
      inputRef={inputRef}
      searchValue={searchValue}
      searchTable={searchTable}
      clearSearchValue={clearSearchValue}
    />
  );
};

export default DeviceManageContainer;
